import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ProjectHelpWidget from "../../shared/widgets/ProjectHelpWidget";
import { PAGE_TYPE_MASTER } from "../../../constants/suggestOptions";
import useSuggestionsIndex from "../../shared/hooks/useSuggestionsIndex";
import ConfirmDeleteDialog from "../../shared/ConfirmDeleteDialog";

const CompanySuggestionsWidget = ({ csrfToken, anchorRef }) => {
  const [pageType, setPageType] = useState(PAGE_TYPE_MASTER[0]?.value ?? "");

  const pageTypes = useMemo(() => (pageType ? [pageType] : []), [pageType]);

  const MAX_WIDGET_SUGGESTIONS = 100;

  // 共通Hookで company 用のサジェスト一覧を取得
  // WidgetはページングUIがないため、取りこぼし防止で多めに取得（最大100件）
  const base = useSuggestionsIndex({
    mode: "company",
    pageTypes,
    per: MAX_WIDGET_SUGGESTIONS,
  });

  // 削除ダイアログ
  const [showDialog, setShowDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // row
  const [isDeleting, setIsDeleting] = useState(false);

  // 子から受け取る「新規行削除実行関数」
  const [pendingLocalDelete, setPendingLocalDelete] = useState(null);

  /**
   * サジェスト保存処理（company専用）
   * 保存成功後に再取得して最新状態に更新する
   */
  const handleSaveSuggestions = useCallback(
    async (payload) => {
      try {
        const res = await fetch("/company/suggestions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ suggestions: payload }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        toast.success("サジェストを保存しました。");

        // 保存後に再取得してUIを最新化
        await base.refetch();
      } catch (e) {
        console.error(e);
        toast.error("サジェストの保存に失敗しました。");
      }
    },
    [csrfToken, base.refetch],
  );

  // 削除ダイアログを開く
  const handleOpenDeleteDialog = useCallback(
    (row, localDeleteAction = null) => {
      if (!row) return;
      setDeleteTarget(row);
      setPendingLocalDelete(() => localDeleteAction);
      setShowDialog(true);
    },
    [],
  );

  // 削除ダイアログを閉じる（削除中は不可）
  const handleCloseDeleteDialog = useCallback(() => {
    if (isDeleting) return;
    setShowDialog(false);
    setDeleteTarget(null);
    setPendingLocalDelete(null);
  }, [isDeleting]);

  // サジェスト削除処理（company専用）
  const handleDeleteSuggestion = useCallback(
    async (id) => {
      if (!id) return false;

      try {
        setIsDeleting(true);

        const res = await fetch(`/company/suggestions/${id}`, {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        toast.success("サジェストを削除しました。");

        // 削除後に再取得してUIを最新化
        await base.refetch();
        return true;
      } catch (e) {
        console.error(e);
        toast.error("サジェストの削除に失敗しました。");
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [csrfToken, base.refetch],
  );

  // 削除確定
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || isDeleting) return;

    // DBに保存されているもの(idあり)のみ削除リクエストを送る
    if (deleteTarget.id) {
      const isDeleted = await handleDeleteSuggestion(deleteTarget.id);
      if (isDeleted) {
        handleCloseDeleteDialog();
      }
      return;
    }

    // 未保存行は子コンポーネント側の rows から削除する
    pendingLocalDelete?.();
    handleCloseDeleteDialog();
  }, [
    deleteTarget,
    isDeleting,
    handleDeleteSuggestion,
    handleCloseDeleteDialog,
    pendingLocalDelete,
  ]);

  /**
   * Widgetに渡す formState
   */
  const formState = useMemo(
    () => ({
      pageType,
      setPageType,
      suggestions: base.suggestions,
      isSuggestionsLoading: base.isSuggestionsLoading,
      onSave: handleSaveSuggestions,
      onRequestDelete: handleOpenDeleteDialog,
      isDeleting,
    }),
    [
      pageType,
      base.suggestions,
      base.isSuggestionsLoading,
      handleSaveSuggestions,
      handleOpenDeleteDialog,
      isDeleting,
    ],
  );

  return (
    <>
      <ProjectHelpWidget
        mode="company"
        anchorRef={anchorRef}
        formState={formState}
      />

      <ConfirmDeleteDialog
        open={showDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default CompanySuggestionsWidget;
